import { catchError } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, filter, map, of, Subject, takeUntil, tap } from 'rxjs';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';

/**
 * interfaces
*/
import { IUserInfo } from 'src/app/core/interfaces/user/user-info';
import { IAdvertisementResponse } from 'src/app/core/interfaces/ads/advertisement';
import { IHttpErrorResponse } from 'src/app/core/classes/http-error-response-handler';
import { IAdvertisementPost, IAdvertisementPostResponse } from 'src/app/core/interfaces/ads/advertisement-post';

/**
 * enums
*/
import { environment } from 'src/environments/environment';
import { FILE_TYPE } from 'src/app/core/enums/global/files-types';
import { CHAT_DATA_TYPE } from 'src/app/core/enums/chat/data-typs';
import { RANDOM_VALUE } from 'src/app/core/enums/global/random-value';
import { LAYOUTS_TYPE } from 'src/app/core/enums/layouts/layouts-types';

/**
 * services
*/
import { AuthService } from 'src/app/core/services/user/auth.service';
import { AlertService } from 'src/app/core/services/alert/alert.service';
import { ApiResponseService } from 'src/app/core/services/handel-api-response/api-response.service';

/**
 * validations
 */
import OverrideValidators from 'src/app/core/validations/validators-override/validators-override';

@Component({
  selector: 'app-advertisement-progress-timeline',
  templateUrl: './advertisement-progress-timeline.component.html',
  styleUrls: ['./advertisement-progress-timeline.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AdvertisementProgressTimelineComponent implements OnInit, AfterViewInit, OnDestroy {

  /************  ViewChild  ************/
  /**
   * ProgressChar
   */
  @ViewChildren('ProgressChar') private _progressChars!: QueryList<ElementRef<HTMLSpanElement>>;

  /**
   * ProgressBar
   */
  @ViewChild('ProgressBar', { read: ElementRef<HTMLDivElement> }) private _progressBar: ElementRef<HTMLDivElement>

  /**
   * ProgressContainer
   */
  @ViewChild('ProgressContainer', { read: ElementRef<HTMLDivElement> }) private _progressContainer: ElementRef<HTMLDivElement>  

  /************  Inputs  ************/
  /**
   * progress
   * @description achievementRatio
   */
  @Input({ alias: 'advertisementData', required: true }) public AdvertisementData: IAdvertisementResponse = null;

  /************  Fields  ************/
  /**
   * _destroy
   */
  private _destroy$ = new Subject<void>();

  /**
   * IsLoading
   * @description loading projectPosts result
   */
  public IsLoading: boolean = false;

    /**
   * CurrentPostsDeleting
   * @description to target current post has trigger to delete to show loading for it while Delete Post
   */
    public CurrentPostsDeleting: number[] = [];

  /**
   * isUploadingFiles
   * @description loading Uploading Files
   */
  public isUploadingFiles: boolean = false;  

  /**
   * @description pagination page
   */
  private _currentPage: number = 1;

  /**
   * @description pagination total page
   */
  private _totalPages: number = 0;

  /**
   * @description Project Posts Result
   */
  public Posts: IAdvertisementPostResponse[] = [];

  /**
   * 
   */
  public Form: FormGroup = null;

  /**
   * 
   */
  public IsCreatePostFormVisible: boolean = false;


  /************  Accessors  ************/

  /**
   * @description to get current auth user
   */
  public get UserInfo(): BehaviorSubject<IUserInfo> {
    return this._auth.userInfo
  }  

  /**
   * Progress
   */
  public get Progress(): number {
    return this.AdvertisementData.achievementRatio;
  }

  public set Progress(value: number) {
    this.AdvertisementData.achievementRatio = value
  }

  /**
   * SplitTexts
   */
  public get SplitText(): string[] {
    return ('نسبة الانجاز ' + this.Progress +'%').split('')
  }

  /**
   * 
   */
  public get IsLoadMore(): boolean {
    return this._currentPage < this._totalPages
  }

  /**
   * CHAT_DATA_TYPE
   */
  public get CHAT_DATA_TYPE(): typeof CHAT_DATA_TYPE{
    return CHAT_DATA_TYPE
  }

  /**
   *
   */
  constructor(
    private _fb: FormBuilder,
    private _auth: AuthService,
    private _alert: AlertService,
    private _cdr: ChangeDetectorRef,
    private _api: ApiResponseService
  ) {}


  /************  Life Cycle Hooks  ************/
  public ngOnInit(): void {
    
    /**
     * init From
     */
    this.Form = this._fb.group({
      media: [null],
      content: [null, OverrideValidators.required],
      achievementRatio: [this.Progress, [OverrideValidators.required, Validators.max(100), Validators.min(this.Progress)]],
    })
    
    /**
     * get advertisement Posts
     */
    this._getPosts(true);

    /** */
    this.Form
      .get('achievementRatio')
      .valueChanges
      .pipe(
        takeUntil(this._destroy$),
        map(value => parseInt(value)),
        tap(value => {

          /**
           * 
           */
          if (value > 100) this.Form.get('achievementRatio').patchValue(100)
        }),
        filter(value => value <= 100)
      )
      .subscribe()

  }

  /**
   * 
   */
  public ngAfterViewInit(): void {

    /**
     * _progressChars
     */
    if(this._progressChars.length > 0) {

      /**
       * 
      */
      setTimeout(() => this._updateProgressText(), 1)

    }    

  }

  /**
   * 
   */
  private _updateProgressText(): void {

    // init
    const { clientWidth: progressBarWidth } = this._progressBar.nativeElement
    const { clientWidth: progressContainerWidth } = this._progressContainer.nativeElement

    /**
    * 
    */
    for (let index = 0; index < this._progressChars.length; index++) {

      // init
      const span = this._progressChars.get(index);
      const spanHalfWidth = Math.ceil(span.nativeElement.offsetWidth / 2)
      // console.log(x)
      /**
      * 
      */
      if (progressBarWidth >= (progressContainerWidth - (span.nativeElement.offsetLeft + spanHalfWidth))) {
        span.nativeElement.classList.add('text-white');
      }
      else break;

    }    

  }

  /**
   * 
   */
  public trackByChar(index: number, char: string): string {
    return char
  }

  /**
   * 
   */
  public trackById(index: number, post: IAdvertisementPostResponse): number {
    return post.id
  }  


  /**
   * 
   * @param refresh true then get new Data, like after create post, false then get data as int or new pagination page
   * @returns 
   */
  public _getPosts(refresh?: boolean): void {

    /**
     * handler loading or null new data
     */
    if (this.IsLoading) return;

    // ES6 Destruction
    const { id } = this.AdvertisementData;


    /**
     * start loading
     */
    this.IsLoading = true;

    /**
     * 
     */
    this._api
      .AdvertisementPosts(id, { params: { 
        limit: 5,
        page: this._currentPage,
        [RANDOM_VALUE.loader]: true
      }})
      .subscribe({
        next: res => {

          // ES6 Destruction
          const { data, page, pageCount } = res;

          /**
           * 
           */
          const filter = data.filter(_post => !this.Posts.map(post => post.id).includes(_post.id))
          
          /**
           * 
           */
          if (refresh) this.Posts = data
          else {

            if (this.Posts.length > 0) this.Posts.push(...filter);
            else this.Posts = filter;

          }

          /**
           * 
           */
          this._cdr.detectChanges();

          /**
           * 
           */
          this._currentPage = page;

          /**
           * 
           */
          this._totalPages = pageCount;

          /**
           * stop loading
           */
          this.IsLoading = false;

        },
        error: _ => this.IsLoading = false
      })    

  }

  /**
   * 
   */
  public NextPage(): void {
    
    /**
     * 
     */
    this._currentPage += 1;

    /**
     * 
     */
    this._getPosts();

  }

  /**
   * ToggleCreatePostFormVisible
   */
  public ToggleCreatePostFormVisible(): void {
    this.IsCreatePostFormVisible = !this.IsCreatePostFormVisible
  }

  /**
   * CloseForm
   */
  public CloseForm(): void {

    /**
     * 
     */
    this.ToggleCreatePostFormVisible();

    /**
     * 
     */
    this.Form.reset({achievementRatio: this.Progress});

  }

  /**
   * 
   */
  public InputChange(event: Event): void {

    /**
     * init
     */
    const el = (event.target as HTMLDivElement);

    /**
     * 
     * clear input;
     */
    if (el.textContent.length === 0) el.textContent = "";

    /**
     * 
     */
    this.Form.get('content').patchValue(el.textContent)

  }
  
  /**
   * 
   */
  private async _createThumbnail(url: string): Promise<File> {

    
    return new Promise((res, rej) => {
      
      /**
       * init
       */
      const video = document.createElement('video');

      /**
       * set
       */
      video.src = url;
      video.crossOrigin = 'anonymous';

      /**
       * 
       */
      video.addEventListener('loadeddata', () => {
  
        video.currentTime = Math.floor(video.duration / 2);
  
        video.addEventListener('seeked', () => {
  
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
      
          const ctx = canvas.getContext('2d');
          
          if (ctx) {


            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(blob => {

              const file = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });

              res(file)

            }, 'image/jpeg', 0.8);

          }
          else rej(new Error('Unable to get canvas context'))
          
        })
      })
    })

  }

  /**
   * 
   */
  public UploadFile(event: Event): void {

    /**
     * 
     */
    this.Form.get('media').reset();
    this.Form.get('media').markAsTouched();

    /**
     * init
     */
    let isValid = false;
    const formData= new FormData()
    const input = (event.target as HTMLInputElement);
    const filesType = [FILE_TYPE.img, FILE_TYPE.mp4, FILE_TYPE.mp3]
    
    // ES6 Destruction
    const { files } = input;

    /**
     * 
     */
    for (let index = 0; index < files.length; index++) {
      
      const file = files[index];

      // ES6 Destruction
      const { type } = file

      /**
       * 
       */
      isValid = filesType.some(fileType => type.includes(fileType))

      /**
       * 
       */
      if (!isValid) {
        this.Form.get('media').setErrors({notSupported: 'صيغة الملفات غير مدعومة'})
        break
      }
      else {

        formData.append('imgs', file)

        this.Form.get('media').setErrors(null)

      }

      
    }

    
    if (isValid) {

      /**
       * start loading
       */
      this.isUploadingFiles = true;

      /**
       * init
       */
      let mediaType: CHAT_DATA_TYPE = null;

      /**
       * 
       */
      this._api
        .uploadFilesForAd(formData)
        .pipe(
          filter(res => {

            /**
             * 
             */
            if (typeof res === 'object' && res.event instanceof HttpResponse) return true

            return false;

          }),
          map((res: any) => {

            // ES6 Destruction
            const { event } = res;

            if (event instanceof HttpResponse) {

              /**
               * stop loading
               */
              this.isUploadingFiles = false;

              const { body: { imgs } } = event

              const links: string[] = imgs;
              return links.map(link => {
                
                if (link.includes(FILE_TYPE.pdf)) mediaType = CHAT_DATA_TYPE.pdf
                else if (link.includes(FILE_TYPE.mp4)) mediaType = CHAT_DATA_TYPE.video
                else mediaType = CHAT_DATA_TYPE.img
                return { mediaType, original: link, thumbnail: `${environment.APP_URL}/assets/img/main_logo.svg` }
                
              })

            }
          }),
          catchError(err => {
            
            /**
             * stop loading
             */
            this.isUploadingFiles = false;

            return of(err)
          })
        )
        .subscribe(medias => this.Form.get('media').patchValue(medias))

    }

  }

  /**
   * DeletePost
   */
  public DeletePost(id: number): void {

    /**
     * current target Loading
     */
    this.CurrentPostsDeleting.push(id);

    /**
     * 
     */
    this._api
      .DeleteAdvertisementPost(id, { params: { [RANDOM_VALUE.loader]: true } })
      .subscribe({
        next: _ => {

          /**
           * remove post from Posts
           */
          this.Posts.splice(this.Posts.findIndex(post => post.id === id), 1)

          /**
           * update Posts
           */
          this._getPosts(),

          /**
           * remove current target Loading
           */
          this.CurrentPostsDeleting.splice(this.CurrentPostsDeleting.indexOf(id) ,1)

        },
        error: (err: IHttpErrorResponse) => {
          
          // ES6 Destruction
          const [ first ] = err.error.errors

          /**
           * 
           */
          this._alert.show({
            msg: first.msg,
            type: LAYOUTS_TYPE.alertFailure,
            callback: () => this._alert.hide()
          })

          /**
           * remove current target Loading
           */
          this.CurrentPostsDeleting.splice(this.CurrentPostsDeleting.indexOf(id) ,1)        

        }
      })

  }

  /**
   * 
   */
  public Submit(): void {

    /**
     * handler invalid statue
     */
    if (this.Form.invalid) return;

    // ES6 Destruction
    const { value } = this.Form;
    const { id } = this.AdvertisementData;
    
    /**
     * init
     */
    const formData: IAdvertisementPost = {} as any;

    /**
     * remove nullable value
     */
    Object.keys(value).forEach(key => {

      if (value[key] && value[key] != null)  formData[key] = value[key]

    })

    /**
     * 
     */
    this._api
    .CreateAdvertisementPost(id, formData)
    .subscribe({
      next: _ => {

        /**
         * 
         */
        this.Progress = this.Form.get('achievementRatio').value;

        /**
         * 
         */
        setTimeout(() => this._updateProgressText(), 1)

        /**
         * 
         */
        this._getPosts(true);

        /**
         * 
         */
        this.CloseForm();

        /**
         * 
         */
        this._alert.show({
          type: LAYOUTS_TYPE.alertSucess,
          msg: 'تم اضافة انجاز بشكل ناجح!',
          callback: () => this._alert.hide()
        })

      },
      error: (err: IHttpErrorResponse) => {

        // ES6 Destruction
        const [ firstError ] = err.error.errors

        /**
         * 
         */
        this.Form.setErrors({ controlError: firstError.msg })        

      }
    })

  }

  /**
   * 
   */
  public ngOnDestroy(): void {
    
    this._destroy$.next();
    this._destroy$.complete();

  }

}
